"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";
import { BsXLg } from "react-icons/bs";
import { generateIcon } from "utils";
import type { Translationtype, AiFiltersFormType } from "utils";
import type { PhotoItemType, VideoItemType } from "../MediaGallery/types";
import { AiFilterProvider } from "../../context";
import { useAiFilterContext } from "../../context";
import { useAiFiltersData } from "../../hooks";
import { AiFiltersImageSelector } from "../AiFilters/AiFiltersImageSelector";
import { convertSelectedFiltersToString } from "../AiFilters/aiFilter.helpers";
import { MagicAIFilter } from "../../pages/reservation/reservationDetails/magicServices/MagicAIFilter";
import { getQuickPaymentPricing } from "../../services/quickPaymentService";
import { toastMessage } from "../toast";
import axios from "axios";
import { getAppConfiguration } from "appConfig";

// aspect ratio number → string mapping
const aspectRatioNumberToString: Record<number, string> = {
  0: "16x9",
  1: "1x1",
  2: "9x16",
  3: "4x3",
  4: "3x4",
};

interface IPremiumServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPhotos: (PhotoItemType | VideoItemType)[];
  reservationId: string;
  t: Translationtype;
  onRemovePhoto?: (photoId: string) => void;
  useBudget?: boolean;
  onGenerationComplete?: () => void;
}

function PremiumServicesModalContent({
  isOpen,
  onClose,
  selectedPhotos,
  reservationId,
  t,
  onRemovePhoto,
  useBudget = false,
  onGenerationComplete,
}: IPremiumServicesModalProps) {
  const { selectedItems, selectItems, selectedTab, setSelectedTab, manageSelectedTab } = useAiFilterContext();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [filterValue, setFilterValue] = useState<AiFiltersFormType | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isBudgetGenerating, setIsBudgetGenerating] = useState(false);
  const prevNameFiltersRef = useRef<string | undefined>(undefined);

  // Pricing state — fetched from backend (only in QuickPayment mode)
  const [pricePerImage, setPricePerImage] = useState<number | null>(null);
  const [priceCurrency, setPriceCurrency] = useState<string>("HUF");
  const [priceLoading, setPriceLoading] = useState(!useBudget);
  const [pricePerVideo, setPricePerVideo] = useState<number | null>(null);
  const [includeVideo, setIncludeVideo] = useState(false);

  const imagesToGenerate = selectedPhotos.length * selectedItems.length;
  // Minimum 1 image required (photo × filter combination)

  const photoCost = pricePerImage ? imagesToGenerate * pricePerImage : 0;
  const videoCost = includeVideo && pricePerVideo ? imagesToGenerate * pricePerVideo : 0;
  const totalCost = photoCost + videoCost;
const totalCostRounded = priceCurrency === "HUF" 
  ? Math.ceil(totalCost) 
  : Math.round(totalCost * 100) / 100;
const currencySymbolMap: Record<string, string> = {
  HUF: "Ft",
  EUR: "€",
  USD: "$",
  GBP: "£",
  RON: "lei", 
};
const currencySymbol = currencySymbolMap[priceCurrency] || priceCurrency;
const totalCostDisplay = priceCurrency === "HUF"
  ? totalCostRounded.toString()
  : totalCostRounded % 1 === 0 
    ? totalCostRounded.toString() 
    : totalCostRounded.toFixed(2);
const totalCostFormatted = priceLoading
  ? "..."
  : `${currencySymbol}${totalCostDisplay}`;

  const formatPrice = (price: number) => {
    if (priceCurrency === "HUF") return Math.ceil(price).toString();
    return price % 1 === 0 ? price.toString() : price.toFixed(2);
  };

  const {
    formattedImagesList,
    isLoading,
    error,
    fetchFilters,
  } = useAiFiltersData({ reservationId, publicAccess: true });

  // Fetch filters when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchFilters();
      setIsRedirecting(false);
    }
  }, [isOpen, fetchFilters]);

  // Fetch pricing from backend when modal opens (only in QuickPayment mode)
  useEffect(() => {
    if (!isOpen || useBudget) return;
    setPriceLoading(true);

    const firstPhoto = selectedPhotos.find(
      (p) => "photoSessionId" in p && p.photoSessionId
    );
    const sessionId =
      firstPhoto && "photoSessionId" in firstPhoto
        ? firstPhoto.photoSessionId
        : undefined;

    getQuickPaymentPricing(undefined, sessionId ?? undefined)
      .then((pricing) => {
        if (pricing.found && pricing.qpPhotoCost) {
          setPricePerImage(pricing.qpPhotoCost);
          setPriceCurrency(pricing.currencyCode || "");
          // Video pricing
          if (pricing.qpVideoCost != null && pricing.qpVideoCost > 0) {
            setPricePerVideo(pricing.qpVideoCost);
          } else {
            setPricePerVideo(null);
          }
          console.log(
            `[PremiumServices] Loaded pricing: ${pricing.qpPhotoCost} ${pricing.currencyCode}/image`
          );
        } else {
          console.warn(
            "[PremiumServices] No QPPhotoCost found in backend"
          );
          setPricePerImage(null);
        }
      })
      .catch((err) => {
        console.error("[PremiumServices] Failed to fetch pricing:", err);
        setPricePerImage(null);
      })
      .finally(() => {
        setPriceLoading(false);
      });
  }, [isOpen, useBudget, selectedPhotos]);

  const handleFieldChange = useCallback(
    (data: AiFiltersFormType) => {
      const newNameFilters = data.nameFilters || "";
      const prevNameFilters = prevNameFiltersRef.current;

      setFilterValue({ ...data });

      if (prevNameFilters !== newNameFilters) {
        prevNameFiltersRef.current = newNameFilters;
        manageSelectedTab(newNameFilters, selectedItems);
        fetchFilters(newNameFilters);
      }
    },
    [selectedItems, manageSelectedTab, fetchFilters]
  );

  const handleApply = useCallback(async () => {
    if (selectedItems.length === 0 || imagesToGenerate < 1) {
      return;
    }
    if (!useBudget && priceLoading) {
      return;
    }

    const firstPhoto = selectedPhotos.find(
      (p) => "photoSessionId" in p && p.photoSessionId
    );
    const sessionId =
      firstPhoto && "photoSessionId" in firstPhoto
        ? firstPhoto.photoSessionId
        : null;

    const aspectRatioNum = filterValue?.aspectRatio ?? 0;
    const aspectRatioStr = aspectRatioNumberToString[aspectRatioNum] || "16x9";

    if (useBudget) {
      // Budget mode: directly call the API with the current reservationId
      setIsBudgetGenerating(true);
      try {
        const appConfiguration = await getAppConfiguration();
        const apiUrl = `${appConfiguration.apiUrl}/api/Media/queueGalleryAiPhoto`;

        await axios.post(apiUrl, {
          reservationId,
          sessionId,
          photoIds: selectedPhotos.map((p) => p.id),
          filterIds: formattedImagesList
            .filter((item) => selectedItems.includes(item.name))
            .map((item) => item.id),
          aiPremium: true,
          aspectRatio: aspectRatioStr,
          includeVideo,
        });

        toastMessage.success(t("Gallery.aiGenerationStarted"));
        onGenerationComplete?.();
        onClose();
      } catch (err: any) {
        console.error("[PremiumServices] Budget generation failed:", err);
        toastMessage.error(t("Gallery.error"));
      } finally {
        setIsBudgetGenerating(false);
      }
      return;
    }

    // QuickPayment mode
    const premiumServiceData = {
      reservationId,
      photoIds: selectedPhotos.map((p) => p.id),
      filterIds: formattedImagesList
        .filter((item) => selectedItems.includes(item.name))
        .map((item) => item.id),
      aiPremium: true,
      sessionId,
      aspectRatio: aspectRatioStr,
      includeVideo,
    };
    sessionStorage.setItem(
      "premiumServicePendingGeneration",
      JSON.stringify(premiumServiceData)
    );

    const params = new URLSearchParams();
    params.set("amount", totalCostRounded.toString());
    params.set("numberOfPhotos", imagesToGenerate.toString());
    params.set("currency", priceCurrency);
    params.set("includeVideo", includeVideo.toString());
    params.set("returnUrl", pathname);
    if (sessionId) {
      params.set("sessionId", sessionId);
    }
    const targetUrl = `/${locale}/quickPayment?${params.toString()}`;
    setIsRedirecting(true);

    router.push(targetUrl);
  }, [
    selectedItems,
    imagesToGenerate,
    totalCostRounded,
    locale,
    router,
    pathname,
    reservationId,
    selectedPhotos,
    formattedImagesList,
    filterValue,
    priceLoading,
    includeVideo,
    priceCurrency,
    useBudget,
    onGenerationComplete,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  const reservationDetails = {
    aspectRatio: 0,
    randomFilter: false,
  };

  return (
    <dialog className="modal" open={isOpen}>
      {isRedirecting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg p-8 text-center shadow-xl">
            <div className="loading loading-spinner loading-lg mb-4"></div>
            <p className="font-semibold text-lg">
              {t("Gallery.redirectingToPayment")}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              {t("Global.pleaseWait")}
            </p>
          </div>
        </div>
      )}
      <div className="modal-box w-full h-full max-w-full max-h-full rounded-none overflow-y-auto">
        <div className="modal-content relative z-20">
          <div className="flex items-center mb-4">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-outline mr-3"
            >
              {t("Buttons.goBack")}
            </button>
            <h1 className="text-2xl font-bold">
              {t("Gallery.premiumServices")}
            </h1>
            <span className="ml-auto cursor-pointer" onClick={handleCancel}>
              {generateIcon(BsXLg, "h-[20px] w-[20px]")}
            </span>
          </div>
          {/* Pricing & video toggle — QuickPayment mode only */}
          {!useBudget && !priceLoading && pricePerImage !== null && (
            <div className="rounded-lg mb-4 border border-base-300 overflow-hidden">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-base-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span>🖼️</span>
                  <span className="text-sm text-gray-500">AI Photo</span>
                  <span className="font-bold text-primary text-lg">
                    {currencySymbol}{formatPrice(pricePerImage)}
                  </span>
                  <span className="text-xs text-gray-400">/ image</span>
                </div>
                {pricePerVideo != null && pricePerVideo > 0 && (
                  <div className="flex items-center gap-2">
                    <span>🎬</span>
                    <span className="text-sm text-gray-500">AI Video</span>
                    <span className="font-bold text-secondary text-lg">
                      +{currencySymbol}{formatPrice(pricePerVideo)}
                    </span>
                    <span className="text-xs text-gray-400">/ image</span>
                  </div>
                )}
              </div>
              {pricePerVideo != null && pricePerVideo > 0 && (
                <label
                  htmlFor="includeVideoToggle"
                  className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                    includeVideo
                      ? "bg-secondary/10 border-t-2 border-secondary"
                      : "bg-white border-t border-base-300 hover:bg-base-100"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🎬</span>
                    <span className="font-semibold">
                      {includeVideo ? "✅ Video included" : "Add Video to each image"}
                    </span>
                  </div>
                  <input
                    id="includeVideoToggle"
                    type="checkbox"
                    className="toggle toggle-secondary toggle-lg"
                    checked={includeVideo}
                    onChange={(e) => setIncludeVideo(e.target.checked)}
                  />
                </label>
              )}
            </div>
          )}
          {/* Video toggle — Budget mode */}
          {useBudget && (
            <div className="rounded-lg mb-4 border border-base-300 overflow-hidden">
              <label
                htmlFor="includeVideoToggleBudget"
                className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                  includeVideo
                    ? "bg-secondary/10 border-t-2 border-secondary"
                    : "bg-white hover:bg-base-100"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🎬</span>
                  <span className="font-semibold">
                    {includeVideo ? "✅ Video included" : "Add Video to each image"}
                  </span>
                </div>
                <input
                  id="includeVideoToggleBudget"
                  type="checkbox"
                  className="toggle toggle-secondary toggle-lg"
                  checked={includeVideo}
                  onChange={(e) => setIncludeVideo(e.target.checked)}
                />
              </label>
            </div>
          )}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              {t("Gallery.selectedPhotos")} ({selectedPhotos.length})
            </h2>
            <div className="flex flex-row space-x-3 overflow-auto h-[120px] bg-base-200 p-3 rounded-lg">
              {selectedPhotos.map((photo) => (
                <div key={photo.id} className="flex-shrink-0 relative">
                  <Image
                    src={photo.thumbnail}
                    alt={t("Gallery.selectedPhoto")}
                    width={100}
                    height={100}
                    className="rounded-lg object-cover h-[100px] w-auto"
                  />
                  {onRemovePhoto && (
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      onClick={() => onRemovePhoto(photo.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <MagicAIFilter
            t={t}
            handleFieldChange={handleFieldChange}
            reservationDetails={reservationDetails}
            showRandomFilter={false}
          />

          <AiFiltersImageSelector
            t={t}
            showLoader={isLoading || (!useBudget && priceLoading)}
            imagesList={formattedImagesList}
            handleSaveFilters={handleApply}
            selectedItems={selectedItems}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            buttonLabel={useBudget ? t("Gallery.generateWithBudget") : t("Gallery.payAndGenerateAI")}
            totalCost={!useBudget && imagesToGenerate >= 1 ? totalCostFormatted : undefined}
            imagesToGenerate={imagesToGenerate}
            isButtonDisabled={
              imagesToGenerate < 1 ||
              selectedItems.length === 0 ||
              isRedirecting ||
              isBudgetGenerating ||
              (!useBudget && (priceLoading || pricePerImage === null))
            }
            minImagesMessage={
              !useBudget && pricePerImage === null && !priceLoading
                ? t("Gallery.pricingUnavailable")
                : undefined
            }
          />
        </div>
      </div>

      <form method="dialog" className="modal-backdrop">
        <button onClick={handleCancel}>{t("Buttons.close")}</button>
      </form>
    </dialog>
  );
}

export function PremiumServicesModal(props: IPremiumServicesModalProps) {
  return (
    <AiFilterProvider>
      <PremiumServicesModalContent {...props} />
    </AiFilterProvider>
  );
}